import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import dayjs from 'dayjs';

import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  InputNumber,
  Row,
  TimePicker,
  message,
} from 'antd';

import { bulkShiftUpdateCreate } from 'app/redux/actions/initiatives';

const ShiftsForm = ({ shiftsList, initiativeID }) => {
  const dispatch = useDispatch();

  const [errorMessage, setErrorMessage] = useState(null);

  const [inputFields, setInputFields] = useState(() => {
    if (shiftsList?.length > 0)
      return shiftsList.map(obj => {
        const shift = obj?.node;
        return {
          pk: shift.pk,
          starting_date: shift?.startingDate,
          ending_date: shift?.endingDate,
          starting_time: shift.startingTime,
          ending_time: shift.endingTime,
          check_in_earliest: shift.checkInEarliest,
          check_in_latest: shift.checkInLatest,
          check_out_latest: shift.checkOutLatest,
        };
      });
    else
      return [
        {
          starting_date: '',
          ending_date: '',
          starting_time: '',
          ending_time: '',
          check_in_earliest: '',
          check_in_latest: '',
          check_out_latest: '',
        },
      ];
  });

  const handleFormChange = (index, name, value) => {
    let data = [...inputFields];
    data[index][name] = value
      ? name === 'starting_time' || name === 'ending_time'
        ? value.format('HH:mm').toString()
        : name === 'starting_date' || name === 'ending_date'
        ? value.format('YYYY-MM-DD').toString()
        : value
      : value;
    setInputFields(data);
  };

  const addFields = () => {
    const newField = {
      starting_date: '',
      ending_date: '',
      starting_time: '',
      ending_time: '',
      check_in_earliest: '',
      check_in_latest: '',
      check_out_latest: '',
    };
    setInputFields([...inputFields, newField]);
  };

  const removeFields = index => {
    let data = [...inputFields];
    data.splice(index, 1);
    setInputFields(data);
  };

  const onFinish = async () => {
    const response = await dispatch(
      bulkShiftUpdateCreate(initiativeID, inputFields),
    );

    if (typeof response === 'string' && response.includes('Error'))
      setErrorMessage(response);
    else {
      setErrorMessage(null);

      message.config({
        top: 0, // push to the top
      });

      message.success({
        content: 'Updated Successfully',
        duration: 2, // Displays the success message for 2 seconds
      });

      // Wait for the success message to disappear before reloading
      setTimeout(() => {
        window.location.reload();
      }, 2000); // Matches the duration of the success message
    }
  };

  return (
    <Form onFinish={onFinish}>
      {errorMessage && <p className="text-error">{errorMessage}</p>}

      <Form.List
        name="shiftsForm"
        initialValue={inputFields.map(obj => ({
          ...obj,
          starting_date: obj.starting_date
            ? dayjs(obj.starting_date, 'YYYY-MM-DD')
            : '',
          ending_date: obj.ending_date
            ? dayjs(obj.ending_date, 'YYYY-MM-DD')
            : '',
          starting_time: obj.starting_time
            ? dayjs(obj.starting_time, 'HH:mm')
            : '',
          ending_time: obj.ending_time ? dayjs(obj.ending_time, 'HH:mm') : '',
        }))}
      >
        {(fields, { add, remove }) => (
          <>
            {fields.map((field, index) => (
              <Card
                title={'Shift #' + (index + 1)}
                className="mb-3"
                key={field.key}
                extra={
                  !('pk' in inputFields[index]) && (
                    <Button
                      onClick={() => {
                        removeFields(index);
                        remove(field.name);
                      }}
                    >
                      Remove
                    </Button>
                  )
                }
              >
                <Row gutter={[8, 8]} key={field.key}>
                  <Col md={6} xs={24}>
                    <Form.Item
                      name={[index, 'starting_date']}
                      label="Starting Date"
                      rules={[
                        {
                          required: true,
                          message: "'starting_date' is required",
                        },
                      ]}
                    >
                      <DatePicker
                        name="starting_date"
                        format="YYYY-MM-DD"
                        style={{ width: '100%' }}
                        onChange={value =>
                          handleFormChange(index, 'starting_date', value)
                        }
                      />
                    </Form.Item>
                  </Col>

                  <Col md={6} xs={24}>
                    <Form.Item
                      name={[index, 'ending_date']}
                      label="Ending Date"
                      rules={[
                        {
                          required: true,
                          message: "'ending_date' is required",
                        },
                      ]}
                    >
                      <DatePicker
                        name="ending_date"
                        format="YYYY-MM-DD"
                        style={{ width: '100%' }}
                        onChange={value =>
                          handleFormChange(index, 'ending_date', value)
                        }
                      />
                    </Form.Item>
                  </Col>

                  <Col md={6} xs={24}>
                    <Form.Item
                      name={[index, 'starting_time']}
                      label="Starting Time"
                      rules={[
                        {
                          required: true,
                          message: "'starting_time' is required",
                        },
                      ]}
                    >
                      <TimePicker
                        name="starting_time"
                        format="HH:mm"
                        use12Hours
                        style={{ width: '100%' }}
                        onChange={value =>
                          handleFormChange(index, 'starting_time', value)
                        }
                      />
                    </Form.Item>
                  </Col>

                  <Col md={6} xs={24}>
                    <Form.Item
                      name={[index, 'ending_time']}
                      label="Ending Time"
                      rules={[
                        {
                          required: true,
                          message: "'ending_time' is required",
                        },
                      ]}
                    >
                      <TimePicker
                        name="ending_time"
                        format="HH:mm"
                        use12Hours
                        style={{ width: '100%' }}
                        onChange={value =>
                          handleFormChange(index, 'ending_time', value)
                        }
                      />
                    </Form.Item>
                  </Col>

                  <Col md={8} xs={24}>
                    <Form.Item
                      name={[index, 'check_in_earliest']}
                      label="Check-In Earliest (In Minutes)"
                      rules={[
                        {
                          required: true,
                          message: "'check_in_earliest' is required",
                        },
                      ]}
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        name="check_in_earliest"
                        onChange={value =>
                          handleFormChange(index, 'check_in_earliest', value)
                        }
                      />
                    </Form.Item>
                  </Col>

                  <Col md={8} xs={24}>
                    <Form.Item
                      name={[index, 'check_in_latest']}
                      label="Check-In Latest (In Minutes)"
                      rules={[
                        {
                          required: true,
                          message: "'check_in_latest' is required",
                        },
                      ]}
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        name="check_in_latest"
                        onChange={value =>
                          handleFormChange(index, 'check_in_latest', value)
                        }
                      />
                    </Form.Item>
                  </Col>

                  <Col md={8} xs={24}>
                    <Form.Item
                      name={[index, 'check_out_latest']}
                      label="Check-Out Latest (In Minutes)"
                      rules={[
                        {
                          required: true,
                          message: "'check_out_latest' is required",
                        },
                      ]}
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        name="check_out_latest"
                        onChange={value =>
                          handleFormChange(index, 'check_out_latest', value)
                        }
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            ))}

            <Row gutter={[8, 8]}>
              <Col>
                <Form.Item>
                  <Button
                    onClick={() => {
                      addFields();
                      add();
                    }}
                  >
                    Add More
                  </Button>
                </Form.Item>
              </Col>

              <Col>
                <Button htmlType="submit">Save Changes</Button>
              </Col>
            </Row>
          </>
        )}
      </Form.List>
    </Form>
  );
};

export default ShiftsForm;
